import React from 'react';
import { ToastContext, Dropdown, Modal, Button } from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';
import useAppContext from '../../../context/useAppContext';
import PropTypes from 'prop-types';
import SessionModel from '../../../models/sessionModel';
import SelectTaxes from '../../../components/tax/selectTaxes';

export function TaxModal({ items = null, onSubmit = () => {}, onClose = () => {}, loading = false, currentTaxId = null, taxes = null }) {
    const { t } = useTranslation();
    const [taxId, setTaxId] = React.useState(currentTaxId);

    React.useEffect(() => {
        setTaxId(currentTaxId);
    }, [currentTaxId]);

    const _onSubmit = React.useCallback(() => {
        onSubmit(taxId === 'event' ? null : taxId);
    }, [taxId, onSubmit]);

    if (!items || items.length === 0) {
        return null;
    }

    return (
        <Modal
            open={Boolean(items)}
            onClose={onClose}
            title={t('ticket.sessions.actions.tax.modalTitle')}
            submitButton={<Button primary disabled={loading} isLoading={loading} onClick={_onSubmit} label={t('ticket.common.action.save')} />}
            cancelButton={<Button disabled={loading} onClick={onClose} label={t('ticket.common.action.cancel')} />}
        >
            <SelectTaxes label={t('ticket.common.taxRate')} taxes={taxes} onChange={(optionValue, option) => setTaxId(optionValue)} value={taxId} withOptionTaxEvent />
        </Modal>
    );
}

TaxModal.propTypes = {
    objectToDelete: PropTypes.array,
    onSubmit: PropTypes.func,
    onClose: PropTypes.func,
    loading: PropTypes.bool,
    currentTaxId: PropTypes.string,
    taxes: PropTypes.array
};

export function TaxAction({ objects: selectedSessions, taxes = [], onSave = () => {} }) {
    const [itemsToAction, setItemsToAction] = React.useState([]);
    const toast = React.useContext(ToastContext);
    const { t } = useTranslation();
    const [{ event, organization }] = useAppContext();

    const [isSubmitting, setIsSubmitting] = React.useState(false);

    const itemsFiltered = React.useMemo(() => {
        if (!selectedSessions) {
            return [];
        }

        return selectedSessions;
    }, [selectedSessions]);

    const taxId = React.useMemo(() => {
        if (selectedSessions.length !== 1 || itemsFiltered.length !== 1) {
            return 'event';
        }

        if (!selectedSessions || !selectedSessions.length || !itemsFiltered.length) {
            return 'event';
        }

        return itemsFiltered[0].tax_id !== null ? Number(itemsFiltered[0].tax_id) : 'event';
    }, [selectedSessions, itemsFiltered]);

    const handleSubmit = React.useCallback(
        taxId => {
            setIsSubmitting(true);
            SessionModel.perform_action('tax', itemsToAction, { organization, event, tax_id: taxId })
                .then(() => {
                    onSave();
                    toast.success(t(`ticket.sessions.actions.tax.success`));
                    setItemsToAction(null);
                })
                .catch(e => {
                    console.error(e);
                    toast.error(t(`ticket.sessions.actions.tax.error`));
                })
                .finally(() => {
                    setIsSubmitting(false);
                });
        },
        [event, organization, SessionModel, toast, itemsToAction, taxId, onSave]
    );

    return (
        <>
            <TaxModal
                items={itemsToAction}
                taxes={taxes}
                onClose={() => setItemsToAction(null)}
                onSubmit={handleSubmit}
                totalItemsSelected={selectedSessions.length}
                loading={isSubmitting}
                currentTaxId={taxId}
            />
            <Dropdown.Item disabled={!itemsFiltered.length} onClick={() => setItemsToAction(itemsFiltered.length > 0 ? itemsFiltered : null)}>
                {t('ticket.sessions.actions.tax.label')}
            </Dropdown.Item>
        </>
    );
}

TaxAction.propTypes = {
    objects: PropTypes.array.isRequired, //required
    taxes: PropTypes.array,
    onSave: PropTypes.func
};
